import { useEffect, useState, type CSSProperties } from 'react';
import { directorClock, directorTeamName, type DirectorLanguage, type DirectorProfile, type Fan, type GameMeta, type Invite, type LiveGame, type LiveGameContext } from '@clutch/core';
import { navigate } from '../router';
import { useClient } from '../client';
import { firstName, initials } from '../room/derive';
import { teamAccent } from '../ui/color';
import { Headshot, TeamLogo } from '../ui/Media';
import { buildFeed, type FeedCard, type FeedTeam } from '../home/feed';
import type { StreamStatus } from '../api';
import { FanNav } from '../ui/Nav';
import { discoveryGames, type DiscoveryGame } from '../home/discovery';
import { PersonalHome } from '../home/PersonalHome';
import { DiscoveryRows } from '../home/DiscoveryRows';
import { discoveryRows } from '../home/featured';
import { currentWeek } from '../home/personal';
import { interests } from '../home/preferences';
import { cardHeadline, cardStatus, discoveryCopy, feedDelay, homeCopy, localizedActivity, weekCopy, cardDate } from '../home/language';
import { htmlLang, pick } from '../director/i18n';

export function Home({ fan, onEdit, onJoin }: { fan: Fan; onEdit: () => void; onJoin: (roomId: string) => void }) {
  const client = useClient();
  const [view, setView] = useState<'live' | 'replays'>('live');
  const [invites, setInvites] = useState<{ live?: Invite; replays?: Invite }>({});
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const [live, setLive] = useState<LiveGame[]>([]);
  const [contexts, setContexts] = useState<LiveGameContext[]>([]);
  const [liveLoaded, setLiveLoaded] = useState(false);
  const [scheduleError, setScheduleError] = useState(false);
  const [partial, setPartial] = useState(false);
  const [retry, setRetry] = useState(0);
  const [replays, setReplays] = useState<GameMeta[]>();
  const [opening, setOpening] = useState<string>();
  const [err, setErr] = useState<string>();
  const [profile, setProfile] = useState<DirectorProfile>();
  const lang = profile?.language ?? fan.directorProfile?.language ?? 'en';
  const copy = homeCopy(lang);
  const week = weekCopy(lang);

  useEffect(
    () =>
      client.subscribeFan(
        fan.fanId,
        (e) => {
          if (e.kind === 'invite') setInvites((cur) => ({ ...cur, [e.data.mode === 'live' ? 'live' : 'replays']: e.data }));
          else setInvites((cur) => Object.fromEntries(Object.entries(cur).map(([key, invite]) => [key,
            invite.inviteId === e.data.inviteId ? { ...invite, facts: { ...invite.facts, friendCalls: e.data.friendCalls } } : invite,
          ])));
        },
        setStatus,
      ),
    [client, fan.fanId],
  );

  useEffect(() => {
    let alive = true;
    let loading = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const loadLive = () => {
      if (!alive || document.hidden || loading) return;
      loading = true;
      void Promise.all([client.liveGames(), client.liveContext?.().catch(() => []) ?? Promise.resolve([])])
        .then(([g, observed]) => { if (alive) { setLive(g); setContexts(observed); setScheduleError(false); setPartial(!!client.scheduleCoverage?.().missingDates.length); } })
        .catch(() => { if (alive) { setLive([]); setScheduleError(true); } })
        .finally(() => {
          loading = false;
          if (!alive) return;
          setLiveLoaded(true);
          if (!document.hidden) timer = setTimeout(loadLive, 10_000);
        });
    };
    const onVisibility = () => {
      clearTimeout(timer);
      loadLive();
    };
    document.addEventListener('visibilitychange', onVisibility);
    loadLive();
    client
      .listGames()
      .then((g) => alive && setReplays(g))
      .catch(() => alive && setReplays([]));
    return () => {
      alive = false;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [client, retry]);

  const feed = buildFeed(live, replays ?? []);
  const current = client.mode === 'fixture' ? feed.live : currentWeek(feed.live);
  const liveNow = current.filter((c) => c.kind === 'live');
  const followed = interests(profile, fan);
  const discovery = discoveryGames(live.filter((g) => current.some((c) => c.gameId === g.gameId)), contexts, { team: followed.teams[0] });
  const featured = discovery[0];
  const rest = discovery.slice(1);
  const tonight = current.filter((c) => c.kind === 'tonight');
  const finished = current.filter((c) => c.kind === 'final').sort((a, b) => (b.startUtc ?? '').localeCompare(a.startUtc ?? ''));

  const createFor = async (c: FeedCard, recap = false) => {
    const { roomId } =
      c.kind === 'classic'
        ? await client.createRoom({ gameId: c.gameId, startAtSeq: c.startAtSeq ?? 0, speed: 1 })
        : await client.createRoom({ gameId: c.gameId, ...(recap && c.kind === 'final' ? { mode: 'recap' as const } : { community: c.kind === 'live' || c.kind === 'tonight' }) });
    return roomId;
  };
  const open = async (c: FeedCard) => {
    if (opening) return;
    setOpening(c.key);
    setErr(undefined);
    try {
      onJoin(await createFor(c));
    } catch {
      setErr(copy.failed);
      setOpening(undefined);
    }
  };
  const openForCatchUp = async (c: FeedCard) => {
    setErr(undefined);
    try {
      return await createFor(c, true);
    } catch {
      setErr(copy.failed);
      return undefined;
    }
  };

  const openRecap = async (c: FeedCard) => {
    if (opening) return;
    setOpening(c.key);
    const roomId = await openForCatchUp(c);
    if (roomId) navigate(`/room/${encodeURIComponent(roomId)}/catchup?lang=${lang}`);
    else setOpening(undefined);
  };

  /** Any game → the Director sheet: catch-ups open in "direct" mode; tonight's games open the lobby. */
  const direct = async (c: FeedCard) => {
    if (opening) return;
    if (c.kind === 'tonight') return open(c);
    setOpening(c.key);
    const roomId = await openForCatchUp(c);
    if (roomId) navigate(`/room/${encodeURIComponent(roomId)}/catchup?lang=${lang}&direct=1`);
    else setOpening(undefined);
  };

  const accent = fan.team ? teamAccent(fan.team) : 'var(--volt)';
  const invite = invites[view];
  return (
    <main className="home" style={{ '--accent': accent } as CSSProperties} data-testid="home" lang={htmlLang(lang)}>
      <FanNav current="home" language={lang} />
      <header className="home__top">
        <span className="home__greeting">{client.director ? firstName(fan.displayName) : pick({ en: `Your courtside, ${firstName(fan.displayName)}.`, hi: `आपकी सीट, ${firstName(fan.displayName)}।`, zh: `${firstName(fan.displayName)}，你的场边席。` }, lang)}</span>
        <span className={`home__pulse${status === 'open' ? ' is-on' : ''}`} title={status === 'open' ? copy.listeningTitle : copy.connecting}>
          <span className="live-dot" />
          {status === 'open' ? copy.listening : status === 'reconnecting' ? copy.reconnecting : copy.connecting}
        </span>
        {client.mode === 'fixture' && <span className="devtag">DEV FIXTURE</span>}
        <button className="avatar avatar--btn" onClick={onEdit} aria-label={copy.edit} data-testid="edit-preferences">
          {initials(fan.displayName)}
        </button>
      </header>

      {client.director && <DiscoveryRows rows={discoveryRows({ live: current, classics: feed.classics, followed: followed.teams })} lang={lang} {...(opening ? { busy: opening } : {})} onDirect={(c) => void direct(c)} />}
      <PersonalHome fan={fan} live={current} scheduleLoaded={liveLoaded} scheduleUnavailable={scheduleError || partial} classics={feed.classics} contexts={contexts} onProfileChange={setProfile} onEdit={onEdit} onOpenCard={openForCatchUp} />
      <div id="feed-games" className={`home__intro${view === 'live' ? ' home__intro--live' : ''}`}><h2 className="home__title">{view === 'live' ? week.week : week.archive}</h2>
      {view === 'replays' && <p className="home__hint">{copy.replayHint}</p>}</div>
      <div className="feed-tabs" role="tablist" aria-label={copy.games}>
        <button id="tab-live" role="tab" aria-selected={view === 'live'} aria-controls="feed-live" onClick={() => setView('live')}>{week.week}{liveNow.length > 0 && <span>{liveNow.length}</span>}</button>
        <button id="tab-replays" role="tab" aria-selected={view === 'replays'} aria-controls="feed-replays" onClick={() => setView('replays')}>{week.archive}</button>
      </div>
      {partial && <p className="alert" role="status">{week.partial}</p>}
      {scheduleError && <p className="alert" role="alert">{week.unavailable} <button className="btn btn--ghost" onClick={() => setRetry((n) => n + 1)}>{week.retry}</button></p>}
      {err && <p className="alert" role="alert">{err}</p>}
      {invite && <InviteCard lang={lang} invite={invite} onJoin={() => onJoin(invite.roomId)} />}

      {view === 'live' ? <div role="tabpanel" id="feed-live" aria-labelledby="tab-live">
      {!liveLoaded && <div className="feed__skeleton" aria-label={copy.loading} aria-busy="true" />}
      {liveLoaded && !scheduleError && !partial && liveNow.length === 0 && finished.length === 0 && <div className="live-empty"><span className="eyebrow">{copy.before}</span><h2>{tonight.length ? copy.next : copy.none}</h2><p>{tonight.length ? copy.beforeHint : copy.noneHint}</p>{!tonight.length && <button className="btn btn--ghost" onClick={() => setView('replays')}>{week.archive}</button>}</div>}

      {featured && <FeaturedGame lang={lang} game={featured} busy={opening === featured.card.key} onOpen={() => open(featured.card)} />}
      {rest.length > 0 && <section className="feed feed--schedule" aria-label={copy.league}>
        <div className="section-heading"><h2>{copy.league}</h2><span className="eyebrow">{rest.length} {copy.moreLive}</span></div>
        <div className="schedule-list">{rest.map((g) => <ScheduleGame lang={lang} key={g.card.key} c={g.card} note={discoveryCopy(g, lang).headline} busy={opening === g.card.key} onOpen={() => open(g.card)} />)}</div>
      </section>}

      {finished.length > 0 && <section className="feed feed--last-night" aria-label={copy.recent}>
        <div className="section-heading"><h2>{week.recent}</h2><span className="eyebrow">{week.full}</span></div>
        <p className="feed__note">{week.recap}</p>
        <div className="feed__rail">{finished.map((c) => <LiveCard recap lang={lang} key={c.key} c={c} busy={opening === c.key} onOpen={() => openRecap(c)} />)}</div>
      </section>}

      {tonight.length > 0 && <section className="feed" aria-label={copy.tonight}><div className="section-heading"><h2>{copy.tonight}</h2><span className="eyebrow">{copy.eastern}</span></div>
        <p className="feed__note">{copy.tonightHint}</p>
        <div className="schedule-list">{tonight.map((c) => <ScheduleGame lang={lang} key={c.key} c={c} note={copy.joinBefore} busy={opening === c.key} onOpen={() => open(c)} />)}</div>
      </section>}

      </div> : <div role="tabpanel" id="feed-replays" aria-labelledby="tab-replays">

      <section className="feed" aria-label={copy.classics}>
        <div className="section-heading"><h2>{week.archive}</h2><span className="eyebrow">{week.archiveHint}</span></div>
        {replays === undefined && <div className="feed__skeleton" aria-busy="true" />}
        <div className="feed__stack">
          {feed.classics.map((c, i) => (
            <ClassicCard lang={lang} key={c.key} c={c} big={i === 0} busy={opening === c.key} onOpen={() => open(c)} />
          ))}
        </div>
      </section>
      </div>}

      <footer className="home__foot">
        <ul className="prefs">
          {followed.teams.map((t) => <li key={t}>{directorTeamName(t, lang)}</li>)}
          {followed.players.map((p) => <li key={p}>{p}</li>)}
          <li>
          {pick({ en: `≤ ${fan.budgetPerWeek} invite${fan.budgetPerWeek === 1 ? '' : 's'} / week`, hi: `हर सप्ताह अधिकतम ${fan.budgetPerWeek} आमंत्रण`, zh: `每周最多 ${fan.budgetPerWeek} 次邀请` }, lang)}
          </li>
        </ul>
        <p className="home__fine">{copy.fine}</p>
      </footer>
    </main>
  );
}

function FeaturedGame({ game, lang, busy, onOpen }: { game: DiscoveryGame; lang: DirectorLanguage; busy: boolean; onOpen: () => void }) {
  const { card: c, context, player } = game;
  const copy = homeCopy(lang);
  const text = discoveryCopy(game, lang);
  const activity = localizedActivity(context, lang);
  const reaction = context?.recentReactions?.filter((r) => Date.now() - Date.parse(r.at) <= 60_000).at(-1);
  const delay = context?.feed.lagMs;
  const source = feedDelay(delay, lang);
  return <article className="discovery-hero" data-testid="featured-game" style={{ '--a': teamAccent(c.away.tricode), '--h': teamAccent(c.home.tricode) } as CSSProperties}>
    <button className="discovery-hero__open" onClick={onOpen} disabled={busy} data-testid="live-card">
      <span className="discovery-hero__top"><span><i className="feed__live-dot" /> {copy.live} · {cardStatus(c, lang)}</span><b>{text.label}</b></span>
      <span className="discovery-hero__headline">{text.headline}</span>
      <span className="discovery-hero__detail">{text.detail}</span>
      <span className="discovery-hero__game">
        <Matchup away={c.away} home={c.home} size={36} />
        {player ? <span className="discovery-hero__player"><Headshot personId={player.personId} source={context!.meta.source} name={player.playerName} size={116} /><span>{player.playerName}</span><small>{copy.observed} · {directorClock(player.period, player.clockSec, lang)}</small></span>
          : <span className="discovery-hero__crests" aria-hidden="true"><TeamLogo tricode={c.away.tricode} size={80} /><TeamLogo tricode={c.home.tricode} size={80} /></span>}
      </span>
      <span className="discovery-hero__cta">{busy ? copy.opening : copy.join} <span aria-hidden="true">↗</span></span>
    </button>
    <div className="discovery-hero__foot">
      {reaction ? <span className="discovery-hero__reaction"><span>{reaction.emoji}</span> {reaction.displayName}{reaction.isTest && <i>{copy.test}</i>} <small>{copy.reacted}</small></span> : activity && <span className="discovery-hero__activity">{activity}</span>}
      {reaction && activity && <span className="discovery-hero__activity">{activity}</span>}
      <small title={copy.sourceTitle}>{source}</small>
    </div>
  </article>;
}

function ScheduleGame({ c, lang, note, busy, onOpen }: { c: FeedCard; lang: DirectorLanguage; note: string; busy: boolean; onOpen: () => void }) {
  const copy = homeCopy(lang);
  return <button className="schedule-game" onClick={onOpen} disabled={busy} data-testid="live-card" aria-label={`${directorTeamName(c.away.tricode, lang)} · ${directorTeamName(c.home.tricode, lang)}, ${cardStatus(c, lang)}. ${note}`}>
    <span className="schedule-game__teams"><span><TeamLogo tricode={c.away.tricode} size={23} /><b>{c.away.tricode}</b>{c.away.score !== undefined && <strong>{c.away.score}</strong>}</span><span><TeamLogo tricode={c.home.tricode} size={23} /><b>{c.home.tricode}</b>{c.home.score !== undefined && <strong>{c.home.score}</strong>}</span></span>
    <span className="schedule-game__context"><span>{c.kind === 'live' && <i className="feed__live-dot" />}{cardStatus(c, lang)}</span><small>{busy ? copy.opening : note}</small></span><span className="schedule-game__arrow" aria-hidden="true">↗</span>
  </button>;
}

function Matchup({ away, home, size = 34 }: { away: FeedTeam; home: FeedTeam; size?: number }) {
  const row = (t: FeedTeam, other: FeedTeam) => (
    <div className={`mu__row${t.score !== undefined && other.score !== undefined && t.score > other.score ? ' is-lead' : ''}`}>
      <TeamLogo tricode={t.tricode} size={size} />
      <span className="mu__code">{t.tricode}</span>
      {t.score !== undefined && <span className="mu__score">{t.score}</span>}
    </div>
  );
  return (
    <div className="mu">
      {row(away, home)}
      {row(home, away)}
    </div>
  );
}

function LiveCard({ c, lang, recap = false, onOpen, busy }: { c: FeedCard; recap?: boolean; lang: DirectorLanguage; onOpen: () => void; busy: boolean }) {
  const copy = homeCopy(lang);
  const style = { '--a': teamAccent(c.away.tricode), '--h': teamAccent(c.home.tricode) } as CSSProperties;
  return (
    <button className={`lcard lcard--${c.kind}`} style={style} onClick={onOpen} disabled={busy} data-testid="live-card">
      <span className={`lcard__status lcard__status--${c.kind}`}>
        {c.kind === 'live' && <span className="feed__live-dot" />}
        {c.kind === 'live' ? copy.live : c.kind === 'tonight' ? copy.tonight : copy.final} · {cardStatus(c, lang)} · {cardDate(c, lang)}
      </span>
      <Matchup away={c.away} home={c.home} />
      <span className="lcard__cta">{busy ? copy.opening : recap ? weekCopy(lang).catchUp : c.kind === 'live' ? copy.join : c.kind === 'tonight' ? copy.joinBefore : copy.replayFinish}</span>
    </button>
  );
}

function ClassicCard({ c, lang, big, onOpen, busy }: { c: FeedCard; lang: DirectorLanguage; big: boolean; onOpen: () => void; busy: boolean }) {
  const copy = homeCopy(lang);
  const style = { '--a': teamAccent(c.away.tricode), '--h': teamAccent(c.home.tricode) } as CSSProperties;
  const [s1, s2] = c.stars;
  return (
    <button className={`ccard${big ? ' ccard--hero' : ''}`} style={style} onClick={onOpen} disabled={busy} data-testid="classic-card">
      <span className="ccard__bg" aria-hidden="true" />
      {big && (
        <span className="ccard__stars" aria-hidden="true">
          {s1 && <Headshot personId={s1.personId} name={s1.name} size={150} className="ccard__star ccard__star--l" />}
          {s2 && <Headshot personId={s2.personId} name={s2.name} size={150} className="ccard__star ccard__star--r" />}
        </span>
      )}
      <span className="ccard__body">
        <span className="ccard__eyebrow">
          <span className="ccard__replay">{copy.replay}</span> {cardStatus(c, lang)}
        </span>
        <span className="ccard__headline">{cardHeadline(c, lang)}</span>
        <span className="ccard__meta">
          <span className="ccard__score">
            <TeamLogo tricode={c.away.tricode} size={22} />
            {c.away.tricode} {c.away.score}
            <i>–</i>
            {c.home.score} {c.home.tricode}
            <TeamLogo tricode={c.home.tricode} size={22} />
          </span>
          {big && c.stars.length > 0 && (
            <span className="ccard__lines">{c.stars.map((s) => `${s.name.split('-')[0]} ${s.line.replace(' PTS', '')}`).join(' · ')}</span>
          )}
        </span>
        <span className="ccard__cta">{busy ? copy.opening : big ? copy.relive : '→'}</span>
      </span>
    </button>
  );
}

function InviteCard({ invite, lang, onJoin }: { invite: Invite; lang: DirectorLanguage; onJoin: () => void }) {
  const copy = homeCopy(lang);
  const f = invite.facts;
  const parts = f.matchup.split(' @ ');
  const teams = parts.length === 2 ? (parts as [string, string]) : undefined;
  return (
    <article className="invite" role="alert" aria-live="assertive" data-testid="invite">
      <div className="invite__app">
        <span className="invite__icon" aria-hidden="true" />
        <span>{invite.mode === 'live' ? copy.inviteLive : copy.inviteReplay}</span>
        <span className="invite__now">{invite.mode === 'live' ? copy.now : copy.replay}</span>
      </div>
      <h2 className="invite__headline">{lang === 'en' ? invite.headline : copy.inviteHeadline}</h2>
      <p className="invite__facts">
        <span>
          {teams && <TeamLogo tricode={teams[0]} size={18} />}
          {f.matchup}
          {teams && <TeamLogo tricode={teams[1]} size={18} className="invite__logo-r" />}
        </span>
        <span>{f.score}</span>
        <span>{f.clock}</span>
      </p>
      {f.friendCalls.length > 0 && (
        <ul className="invite__friends" aria-label={copy.friends}>
          {f.friendCalls.map((c) => (
            <li key={c.displayName} className="chip chip--friend">
              <span className="avatar avatar--xs">{initials(c.displayName)}</span>
              {firstName(c.displayName)} {copy.called} <b>{c.label}</b>
            </li>
          ))}
        </ul>
      )}
      <button className="btn btn--primary btn--block" onClick={onJoin}>
        {copy.joinMoment}
      </button>
    </article>
  );
}
